(() => {
  if (customElements.get('zxp-energy-carousel')) return;

  class ZxpEnergyCarousel extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.resizeObserver = null;
      this.pageStarts = [];
      this.currentPage = 0;
      this.scrollFrame = null;
    }

    connectedCallback() {
      if (this.dataset.energyReady === 'true') return;

      this.track = this.querySelector('[data-energy-track]');
      this.slides = Array.from(this.querySelectorAll('[data-energy-slide]'));
      this.previousButton = this.querySelector('[data-energy-prev]');
      this.nextButton = this.querySelector('[data-energy-next]');
      this.dots = Array.from(this.querySelectorAll('[data-energy-dot]'));

      if (!this.track || this.slides.length === 0) return;

      this.dataset.energyReady = 'true';
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.previousButton?.addEventListener('click', () => this.goToPage(this.currentPage - 1), { signal });
      this.nextButton?.addEventListener('click', () => this.goToPage(this.currentPage + 1), { signal });
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goToSlide(Number(dot.dataset.energyDot)), { signal });
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.refresh(), { signal });
      }

      this.refresh();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      delete this.dataset.energyReady;
    }

    refresh() {
      const trackWidth = this.track.clientWidth;
      if (!trackWidth) return;

      this.pageStarts = this.slides.reduce((starts, slide, index) => {
        if (index === 0 || slide.offsetLeft - starts[starts.length - 1].offsetLeft >= trackWidth - 2) {
          starts.push(slide);
        }
        return starts;
      }, []);

      const nearest = this.getNearestPage();
      this.updateControls(nearest);
    }

    getNearestPage() {
      if (!this.pageStarts.length) return 0;
      const position = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      this.pageStarts.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - position);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateControls(this.getNearestPage()));
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goToPage(this.currentPage - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goToPage(this.currentPage + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goToPage(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goToPage(this.pageStarts.length - 1);
      }
    }

    goToSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;
      const pageIndex = this.pageStarts.reduce((best, page, candidate) => (
        Math.abs(page.offsetLeft - slide.offsetLeft) < Math.abs(this.pageStarts[best].offsetLeft - slide.offsetLeft)
          ? candidate
          : best
      ), 0);
      this.goToPage(pageIndex);
    }

    goToPage(index) {
      if (!this.pageStarts.length) return;
      const boundedIndex = Math.max(0, Math.min(index, this.pageStarts.length - 1));
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: this.pageStarts[boundedIndex].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.updateControls(boundedIndex);
    }

    updateControls(index) {
      this.currentPage = Math.max(0, Math.min(index, Math.max(this.pageStarts.length - 1, 0)));
      const firstSlideIndex = this.pageStarts[this.currentPage]
        ? this.slides.indexOf(this.pageStarts[this.currentPage])
        : 0;
      if (this.previousButton) this.previousButton.disabled = this.currentPage === 0;
      if (this.nextButton) this.nextButton.disabled = this.currentPage >= this.pageStarts.length - 1;

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === firstSlideIndex;
        dot.classList.toggle('zxp-energy-is-active', active);
        dot.setAttribute('aria-pressed', String(active));
      });
    }
  }

  customElements.define('zxp-energy-carousel', ZxpEnergyCarousel);
})();
